import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '../api/me';
import type { CurrentUser } from '../api/types';
import { ErrorBanner } from '../components/ErrorBanner';
import { GroupsAdminPanel } from '../components/GroupsAdminPanel';
import { useSetPageTitle } from '../components/PageTitle';
import { UsersAdminPanel } from '../components/UsersAdminPanel';

type AdminTab = 'users' | 'groups';

/**
 * Access administration, from either end. **Users** is the default: one row per person, with every group they
 * reach, which is the question asked most often. **Groups** keeps the other reading, which the first cannot
 * replace - who is in a given group, and what that group shares, sharing being a property of a group.
 */
export function GroupAdminPage() {
  const { t } = useTranslation();
  useSetPageTitle(t('admin.title'));

  const [me, setMe] = useState<CurrentUser | null>(null);
  const [tab, setTab] = useState<AdminTab | null>(null);
  const [error, setError] = useState<unknown>(null);

  const reloadMe = useCallback(async () => {
    try {
      const loaded = await getCurrentUser();
      setMe(loaded);
      // Somebody who administers nothing cannot list users at all, so they open on the groups they can read.
      setTab((current) => current ?? (loaded.isGroupAdminSomewhere ? 'users' : 'groups'));
    } catch (caught) {
      setError(caught);
    }
  }, []);

  useEffect(() => {
    void reloadMe();
  }, [reloadMe]);

  const canListUsers = me?.isGroupAdminSomewhere === true;

  return (
    <>
      <ErrorBanner error={error} />

      {canListUsers && (
        <ul className="nav nav-tabs app-tabs" role="tablist" aria-label={t('admin.viewLabel')}>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              className={`nav-link${tab === 'users' ? ' active' : ''}`}
              aria-selected={tab === 'users'}
              onClick={() => setTab('users')}
            >
              <i className="pi pi-user me-2" aria-hidden="true" />
              {t('admin.usersTab')}
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              className={`nav-link${tab === 'groups' ? ' active' : ''}`}
              aria-selected={tab === 'groups'}
              onClick={() => setTab('groups')}
            >
              <i className="pi pi-users me-2" aria-hidden="true" />
              {t('admin.groupsTab')}
            </button>
          </li>
        </ul>
      )}

      {tab === 'users' && canListUsers ? (
        <UsersAdminPanel me={me} onPermissionsChanged={() => void reloadMe()} />
      ) : (
        tab !== null && <GroupsAdminPanel me={me} onPermissionsChanged={() => void reloadMe()} />
      )}
    </>
  );
}
